import React, { useCallback, useEffect, useState, Fragment } from 'react'
import * as qs from 'query-string'
import DefaultErrorMessage from '../../../../../common/components/ErrorBoundary/errorMessage'
import DataTable from '../../../../../app/components/statelessDataTable'
import createColumns from '../columns/columns'
import { usePaidLeave } from '../../../../../context/paidLeaveContext'
import ExportModal from './exportModal'
import {
  addressObjectByType,
  addressStringByType,
  downloadFile,
  filterLocales,
  getStateByCode
} from '../../../../../common/helpers'
import LEGACY_PRODUCTS from '../../../../../common/enum/legacyProducts'
import { PAID_LEAVE_TYPES } from '../../../../../constants/paidLeaveTypes'
import addressTypes from '../../../../../constants/addressTypes'
import sortDirections from '../../../../../constants/sortDirections'
import queryPaidLeaveLocations from '../../../../service/queryPaidLeaveLocations'
import fetchCsvFile from '../../../../service/fetchCsvFile'
import { getCompanyId } from '../../../../helper'
import {CSVDownloadError} from "../../../../../common/components/CSVDownloadError/CSVDownloadError";

const getFirstProductType = (productDict, [productType, productInfo]) => {
  if (PAID_LEAVE_TYPES.includes(productType)) {
    return {
      ...productDict,
      [productType]: productInfo.mostRelevantJurisdiction && productInfo.mostRelevantJurisdiction.type
    }
  }

  return { ...productDict }
}

const mapLocationData = (locations) => locations.map((location) => {
  const { displayName, addresses } = location

  return {
    name: displayName,
    id: location._id,
    address: addressStringByType(addressTypes.SHIPPING, addresses),
    addressObject: addressObjectByType(addressTypes.SHIPPING, addresses),
    productMap: location.productMap && Object.entries(location.productMap).reduce(getFirstProductType, {}),
    hasProducts: location.productMap && Object.keys(location.productMap).length > 0
  }
})

const PaidLeaveLocations = ({ location }) => {
  let parsed = {}
  if (location.search) {
    parsed = qs.parse(location.search)
    parsed.stateAbv = parsed.state
    parsed.stateName = getStateByCode(parsed.state).name
  }
  const filter = parsed.stateAbv
    ? {
      state: {
        facet: { _id: `${parsed.country}~${parsed.stateAbv}` },
        locale: {
          country: parsed.country,
          name: parsed.stateName,
          state: parsed.stateAbv
        }
      }
    }
    : { state: '' }
  const companyId = getCompanyId()
  const { paidLeaveData } = usePaidLeave()
  const [loading, setLoading] = useState()
  const [data, setData] = useState([])
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)

  const [error, setError] = useState(false)
  const [locationCount, setLocationCount] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sortOptions, setSortOptions] = useState({
    field: 'displayName',
    direction: sortDirections.ASCENDING
  })
  const [filters, setFilters] = useState(filter)
  const [filterOptions, setFilterOptions] = useState({
    filter: '',
    field: ''
  })
  const [columns, setColumns] = useState(createColumns({
    sortOptions,
    eventHandlers: {
      setSortOptions,
      setPageNumber
    }
  }))

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [locales, setLocales] = useState([])
  const rowsPerPage = 20

  const handleClose = () => setErrorPopupOpen(false)

  const filterLocalesList = (facets) => {
    const filteredLocales = filterLocales(LEGACY_PRODUCTS.PAID_LEAVE, facets)
    setLocales(filteredLocales)
  }

  useEffect(() => {
    let subscribed = true
    setLoading(true)
    setData([])
    if (paidLeaveData !== null) {
      queryPaidLeaveLocations({
        recordType: 'locations',
        searchText,
        sortOptions,
        filterOptions,
        pageNumber,
        rowsPerPage: 20,
        companyId,
        filters,
        paidLeaveData
      })
        .then((response) => {
          if (subscribed) {
            const { total, locations, localeFacets } = response.data

            filterLocalesList(localeFacets)

            setData(mapLocationData(locations))

            setLocationCount(total)

            setColumns(createColumns({
              sortOptions,
              filterOptions,
              eventHandlers: {
                setSortOptions,
                setFilterOptions,
                setPageNumber
              }
            }))

            setPageNumber(pageNumber)
            setLoading(false)
          }
        })
        .catch((useEffectError) => {
          setLoading(false)
          console.error('useEffectError', useEffectError || 'unknown error')
          setError(useEffectError)
        })
    }

    return () => { subscribed = false }
  }, [pageNumber, filters, searchText, sortOptions, filterOptions, companyId, paidLeaveData])

  const handleChangePage = useCallback((event) => {
    setPageNumber(event.selected + 1)
    setData([])
    setLoading(true)
  }, [])

  const handleSetSearchText = useCallback((value) => {
    setSearchText(value)
  }, [])

  const handleSetSortOptions = useCallback((value) => {
    setSortOptions(value)
  }, [])

  const handleSetFilters = (value) => {
    setFilters(value)
    setPageNumber(1)
  }

  const handleSetPageNumber = useCallback((value) => {
    setPageNumber(value)
  }, [])

  const handleExport = () => {
    setModalIsOpen(true)
  }
  const handleModalClose = () => {
    setModalIsOpen(false)
  }

  const exportCsv = async (product) => {
    setLoading(true)
    try {
      const response = await fetchCsvFile({
        product,
        filters,
        sortOptions,
        searchText,
        companyId
      })
      if (response && response.downloadUrl) {
        downloadFile(response.downloadUrl)
      } else {
        setErrorPopupOpen(true)
        console.error('CSV generation error.')
      }
    } catch (exportCsvError) {
      setErrorPopupOpen(true)
      console.error('Error while fetching csv file ', exportCsvError)
    }
    setLoading(false)
  }

  return (error)
    ? <DefaultErrorMessage error={error} data-testid="error-container" />
    : (
      <Fragment>
        <DataTable
          {...{
            pageCount: Math.ceil(locationCount / rowsPerPage),
            isLoading: loading,
            recordType: 'Locations',
            showSearch: true,
            showExport: true,
            showLocaleFilter: true,
            eventHandlers: {
              handleChangePage,
              setSearchText: handleSetSearchText,
              setSortOptions: handleSetSortOptions,
              setFilters: handleSetFilters,
              export: handleExport,
              setPageNumber: handleSetPageNumber
            },
            columns,
            data,
            filters,
            sortOptions,
            filterOptions,
            pageNumber,
            total: locationCount,
            searchText,
            rowsPerPage,
            locales
          }}
        />
        <ExportModal open={modalIsOpen} close={handleModalClose} exporter={exportCsv} />
        <CSVDownloadError open={errorPopupOpen} onClose={handleClose} />
      </Fragment>
    )
}

export default PaidLeaveLocations
